import type { VoiceAssessmentChartXLabel } from './voice-assessment-score-series'

export type VoiceAssessmentRangeTimeframe = 'weekly' | 'monthly' | 'sixMonths'

export interface VoiceAssessmentRangeBand {
  min: number
  max: number
}

export interface VoiceAssessmentRangePoint {
  x: number
  dayIndex: number
  created: Date
  pitch?: VoiceAssessmentRangeBand
  comfortable?: VoiceAssessmentRangeBand
  segmentId: string
}

export interface VoiceAssessmentRangeSeries {
  points: VoiceAssessmentRangePoint[]
  xLabels: VoiceAssessmentChartXLabel[]
  visibleLabelCount: number
  columnWidthMultiplier: number
  totalDays: number
}

interface VoiceAssessmentRangeSession {
  created: Date
  pitchMin?: string | null
  pitchMax?: string | null
  comfortableMin?: string | null
  comfortableMax?: string | null
}

const DAY_MS = 24 * 60 * 60 * 1000
const X_AXIS_LABEL_COUNT = 7
const MONTHLY_VISIBLE_DAY_COUNT = 31
const DEFAULT_COLUMN_WIDTH_MULTIPLIER = 1
const NOTE_OFFSETS: Record<string, number> = {
  c: 0,
  d: 2,
  e: 4,
  f: 5,
  g: 7,
  a: 9,
  b: 11,
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function startOfLocalMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date)
  const targetDate = next.getDate()
  next.setDate(1)
  next.setMonth(next.getMonth() + months)
  const lastDateOfTargetMonth = new Date(
    next.getFullYear(),
    next.getMonth() + 1,
    0,
  ).getDate()
  next.setDate(Math.min(targetDate, lastDateOfTargetMonth))
  return next
}

function calendarDayDiff(start: Date, end: Date): number {
  return Math.round(
    (startOfLocalDay(end).getTime() - startOfLocalDay(start).getTime()) /
      DAY_MS,
  )
}

function localDayKey(date: Date): string {
  const day = startOfLocalDay(date)
  const year = day.getFullYear()
  const month = String(day.getMonth() + 1).padStart(2, '0')
  const dateOfMonth = String(day.getDate()).padStart(2, '0')
  return `${year}-${month}-${dateOfMonth}`
}

function buildWeeklyLabels(
  start: Date,
  totalDays: number,
  locale: string,
): VoiceAssessmentChartXLabel[] {
  return Array.from({ length: totalDays }, (_, index) => {
    const day = addDays(start, index)
    return {
      line1: day.toLocaleDateString(locale, { weekday: 'short' }),
      line2: day.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
      }),
    }
  })
}

function buildMonthLabels(
  start: Date,
  totalDays: number,
  locale: string,
): VoiceAssessmentChartXLabel[] {
  return Array.from({ length: totalDays }, (_, index) => {
    const daysFromEnd = totalDays - 1 - index
    if (daysFromEnd % 7 !== 0) {
      return { line1: '', line2: '' }
    }

    const day = addDays(start, index)
    const month = day
      .toLocaleDateString(locale, {
        month: 'short',
      })
      .replace(/\.$/, '')
      .toLocaleLowerCase(locale)
    return {
      line1: String(day.getDate()),
      line2: month,
    }
  })
}

function buildSixMonthLabels(
  start: Date,
  totalDays: number,
  locale: string,
): VoiceAssessmentChartXLabel[] {
  const firstMonth = startOfLocalMonth(start)
  const end = addDays(start, totalDays - 1)
  const monthCount =
    (end.getFullYear() - firstMonth.getFullYear()) * 12 +
    (end.getMonth() - firstMonth.getMonth()) +
    1

  return Array.from({ length: monthCount }, (_, index) =>
    addMonths(firstMonth, index).toLocaleDateString(locale, {
      month: 'short',
    }),
  )
}

function visibleLabelCountForTimeframe(
  timeframe: VoiceAssessmentRangeTimeframe,
): number {
  if (timeframe === 'weekly') {
    return 7
  }
  if (timeframe === 'monthly') {
    return MONTHLY_VISIBLE_DAY_COUNT
  }
  if (timeframe === 'sixMonths') {
    return 6
  }
  return X_AXIS_LABEL_COUNT
}

function columnWidthMultiplierForTimeframe(
  timeframe: VoiceAssessmentRangeTimeframe,
): number {
  return DEFAULT_COLUMN_WIDTH_MULTIPLIER
}

export function parseVoiceAssessmentNotePosition(
  note: string | null | undefined,
): number | null {
  const match = note
    ?.trim()
    .toLocaleLowerCase('en-US')
    .match(/^([a-g])([#♯b♭]?)(-?\d+)$/)
  if (match == null) {
    return null
  }

  const [, noteName, accidental, octaveText] = match
  const octave = Number(octaveText)
  const base = NOTE_OFFSETS[noteName ?? '']
  if (base == null || !Number.isFinite(octave)) {
    return null
  }

  const accidentalOffset =
    accidental === '#' || accidental === '♯'
      ? 1
      : accidental === 'b' || accidental === '♭'
        ? -1
        : 0

  return octave * 12 + base + accidentalOffset
}

function parseBand(
  minNote: string | null | undefined,
  maxNote: string | null | undefined,
): VoiceAssessmentRangeBand | undefined {
  const min = parseVoiceAssessmentNotePosition(minNote)
  const max = parseVoiceAssessmentNotePosition(maxNote)
  if (min == null || max == null) {
    return undefined
  }
  return min <= max ? { min, max } : { min: max, max: min }
}

function parseRangeSession(session: VoiceAssessmentRangeSession):
  | (VoiceAssessmentRangeSession & {
      pitch?: VoiceAssessmentRangeBand
      comfortable?: VoiceAssessmentRangeBand
    })
  | null {
  const pitch = parseBand(session.pitchMin, session.pitchMax)
  const comfortable = parseBand(session.comfortableMin, session.comfortableMax)
  if (pitch == null && comfortable == null) {
    return null
  }
  return { ...session, pitch, comfortable }
}

function dedupeLatestRangeSessionByDay(
  sessions: readonly VoiceAssessmentRangeSession[],
) {
  const latestByDay = new Map<string, VoiceAssessmentRangeSession>()

  sessions.forEach((session) => {
    const key = localDayKey(session.created)
    const current = latestByDay.get(key)
    if (
      current == null ||
      session.created.getTime() > current.created.getTime()
    ) {
      latestByDay.set(key, session)
    }
  })

  return [...latestByDay.values()]
    .sort((a, b) => a.created.getTime() - b.created.getTime())
    .map(parseRangeSession)
    .filter(
      (session): session is NonNullable<typeof session> => session != null,
    )
}

export function buildVoiceAssessmentRangeSeries({
  sessions,
  timeframe,
  locale,
  today = new Date(),
}: {
  sessions: readonly VoiceAssessmentRangeSession[]
  timeframe: VoiceAssessmentRangeTimeframe
  locale: string
  today?: Date
}): VoiceAssessmentRangeSeries {
  const end = startOfLocalDay(today)
  const rangedSessions = dedupeLatestRangeSessionByDay(sessions)

  const sixMonthsStart = addMonths(end, -6)
  const earliestRangedDay = rangedSessions[0]?.created
  const start =
    earliestRangedDay != null &&
    startOfLocalDay(earliestRangedDay).getTime() < sixMonthsStart.getTime()
      ? startOfLocalDay(earliestRangedDay)
      : sixMonthsStart
  const totalDays = Math.max(1, calendarDayDiff(start, end) + 1)

  const sessionsInRange = rangedSessions.filter((session) => {
    const day = startOfLocalDay(session.created)
    return day.getTime() >= start.getTime() && day.getTime() <= end.getTime()
  })

  let currentSegment = 0
  let previousDayIndex: number | null = null
  const points = sessionsInRange.map((session) => {
    const dayIndex = calendarDayDiff(start, session.created)
    if (previousDayIndex != null && dayIndex !== previousDayIndex + 1) {
      currentSegment += 1
    }
    previousDayIndex = dayIndex

    return {
      x: totalDays === 1 ? 0.5 : dayIndex / (totalDays - 1),
      dayIndex,
      created: session.created,
      pitch: session.pitch,
      comfortable: session.comfortable,
      segmentId: String(currentSegment),
    }
  })

  const xLabels =
    timeframe === 'weekly'
      ? buildWeeklyLabels(start, totalDays, locale)
      : timeframe === 'monthly'
        ? buildMonthLabels(start, totalDays, locale)
        : buildSixMonthLabels(start, totalDays, locale)

  return {
    points,
    xLabels,
    visibleLabelCount: visibleLabelCountForTimeframe(timeframe),
    columnWidthMultiplier: columnWidthMultiplierForTimeframe(timeframe),
    totalDays,
  }
}
